import { FilterField, FilterValues } from "./types";

const isEmpty = (v: unknown): boolean =>
  v == null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" &&
    !Array.isArray(v) &&
    Object.keys(v as object).length === 0);

const defaultSerialize = (value: unknown): string | undefined => {
  if (isEmpty(value)) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) {
    const list = value
      .map((item) => defaultSerialize(item))
      .filter((s): s is string => s != null && s !== "");
    return list.length ? list.join(",") : undefined;
  }
  if (typeof value === "object") {
    const v = value as { value?: unknown };
    if ("value" in v) return defaultSerialize(v.value);
  }
  return undefined;
};

const defaultParse = (raw: string): string => raw;

/**
 * Дефолтный парсер для мультивыбора (field.multiple): значение в URL
 * хранится строкой через запятую, в форму возвращается массивом строк.
 * Пустые элементы отбрасываются; пустая строка не дойдёт сюда
 * (отфильтрована в deserializeValues).
 */
const defaultParseMultiple = (raw: string): string[] | undefined => {
  const list = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return list.length ? list : undefined;
};

/**
 * Преобразует значения формы фильтра в плоский объект для URL search params.
 * Пустые/неопределённые поля опускаются.
 */
export const serializeValues = (
  values: FilterValues | undefined,
  fields: FilterField[],
): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!values) return out;
  for (const f of fields) {
    for (const key of getFieldKeys(f)) {
      const raw = values[key];
      const serialized = f.serialize ? f.serialize(raw) : defaultSerialize(raw);
      if (serialized != null && serialized !== "") {
        out[key] = serialized;
      }
    }
  }
  return out;
};

/**
 * Преобразует search params (объект из qs) в значения формы фильтра
 * только по описанным полям (чужие ключи игнорируются).
 */
export const deserializeValues = (
  params: Record<string, unknown> | undefined,
  fields: FilterField[],
): FilterValues => {
  const out: FilterValues = {};
  if (!params) return out;
  for (const f of fields) {
    for (const key of getFieldKeys(f)) {
      const raw = params[key];
      if (raw == null || raw === "") continue;
      const str = Array.isArray(raw) ? String(raw[0]) : String(raw);
      const parsed = f.parse
        ? f.parse(str)
        : f.multiple
          ? defaultParseMultiple(str)
          : defaultParse(str);
      // Не кладём undefined в значения формы, чтобы не перекрывать
      // null-дефолты в useUrlFilters (особенность react-hook-form v6).
      if (parsed !== undefined) {
        out[key] = parsed;
      }
    }
  }
  return out;
};

/** Список ключей URL, которыми владеет данный набор фильтров. */
export const getFilterKeys = (fields: FilterField[]): string[] =>
  fields.reduce<string[]>((acc, f) => [...acc, ...getFieldKeys(f)], []);

/** Ключи формы/URL одного поля (у dateRange их два: `name` и `nameTo`). */
const getFieldKeys = (field: FilterField): string[] =>
  field.nameTo ? [field.name, field.nameTo] : [field.name];
